import React from 'react';
import { Pressable, StyleSheet, Switch, Text, TextInput, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { Button } from '@/components/residents/marketplace/shared/Button';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import {
  REDESIGN_SPACING,
  REDESIGN_TYPOGRAPHY,
} from '@/constants/residents/homeConstants';
import { SURVEY_LIMITS } from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type {
  SurveyQuestionConfig,
  SurveyQuestionType,
} from '@/types/residents/corkBoard';

interface Props {
  question: SurveyQuestionConfig;
  onChange: (patch: Partial<SurveyQuestionConfig>) => void;
  onDelete: () => void;
}

const TYPE_LABEL: Record<SurveyQuestionType, string> = {
  poll: 'Poll',
  rating: 'Rating',
  long_text: 'Long text',
};

export const QuestionEditor: React.FC<Props> = ({ question, onChange, onDelete }) => {
  const { colors, components } = useThemeColors();

  return (
    <GlassCard
      variant="soft"
      borderRadius={REDESIGN_SPACING.cardBorderRadius}
      padding={REDESIGN_SPACING.cardPadding}
      style={styles.cardSpacing}
    >
      <View style={styles.headerRow}>
        <View
          style={[
            styles.typeBadge,
            { backgroundColor: colors.newsfeed.announcementBadgeBg },
          ]}
        >
          <Text
            style={[
              REDESIGN_TYPOGRAPHY.label,
              { color: colors.newsfeed.tagActiveText },
            ]}
          >
            {TYPE_LABEL[question.type]}
          </Text>
        </View>
        <Pressable
          onPress={onDelete}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Delete question"
        >
          <Ionicons name="close" size={20} color={colors.accent.red} />
        </Pressable>
      </View>

      <TextInput
        style={[
          styles.promptInput,
          REDESIGN_TYPOGRAPHY.body,
          {
            color: colors.text.primary,
            borderColor: colors.card.border,
            backgroundColor: colors.card.bg,
          },
        ]}
        placeholder="Question prompt"
        placeholderTextColor={colors.text.tertiary}
        value={question.prompt}
        onChangeText={(t) => onChange({ prompt: t.slice(0, SURVEY_LIMITS.promptMaxLength) })}
        multiline
        maxLength={SURVEY_LIMITS.promptMaxLength}
      />

      <View style={styles.requiredRow}>
        <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>Required</Text>
        <Switch
          value={question.isRequired}
          onValueChange={(v) => onChange({ isRequired: v })}
          trackColor={{ false: components.toggle.trackOff, true: components.toggle.trackOn }}
          thumbColor={components.toggle.thumb}
        />
      </View>

      {question.type === 'poll' && (
        <PollOptionsEditor
          options={question.options ?? []}
          onChange={(options) => onChange({ options })}
        />
      )}
      {question.type === 'rating' && (
        <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
          1–5 scale (fixed in v1)
        </Text>
      )}
      {question.type === 'long_text' && (
        <View style={styles.longTextConfig}>
          <Text
            style={[
              REDESIGN_TYPOGRAPHY.small,
              { color: colors.text.secondary, marginBottom: 4 },
            ]}
          >
            Max characters
          </Text>
          <TextInput
            keyboardType="numeric"
            value={String(question.longTextMaxLength ?? SURVEY_LIMITS.longTextDefaultMaxLength)}
            onChangeText={(t) => {
              const n = parseInt(t, 10);
              if (!Number.isNaN(n)) {
                onChange({
                  longTextMaxLength: Math.min(
                    Math.max(n, 1),
                    SURVEY_LIMITS.longTextMaxLength,
                  ),
                });
              }
            }}
            style={[
              styles.numberInput,
              REDESIGN_TYPOGRAPHY.body,
              {
                color: colors.text.primary,
                borderColor: colors.card.border,
                backgroundColor: colors.card.bg,
              },
            ]}
          />
        </View>
      )}
    </GlassCard>
  );
};

const PollOptionsEditor: React.FC<{
  options: { label: string; position: number }[];
  onChange: (options: { label: string; position: number }[]) => void;
}> = ({ options, onChange }) => {
  const { colors } = useThemeColors();

  const update = (i: number, label: string) => {
    onChange(options.map((o, idx) => (idx === i ? { ...o, label } : o)));
  };
  const add = () => {
    if (options.length >= SURVEY_LIMITS.optionsMax) return;
    onChange([...options, { label: '', position: options.length }]);
  };
  const remove = (i: number) => {
    if (options.length <= SURVEY_LIMITS.optionsMin) return;
    onChange(
      options
        .filter((_, idx) => idx !== i)
        .map((o, idx) => ({ ...o, position: idx })),
    );
  };

  return (
    <View style={styles.optionsBlock}>
      {options.map((o, i) => (
        <View key={i} style={styles.optionRow}>
          <TextInput
            style={[
              styles.optionInput,
              REDESIGN_TYPOGRAPHY.body,
              {
                color: colors.text.primary,
                borderColor: colors.card.border,
                backgroundColor: colors.card.bg,
              },
            ]}
            placeholder={`Option ${i + 1}`}
            placeholderTextColor={colors.text.tertiary}
            value={o.label}
            onChangeText={(t) => update(i, t.slice(0, SURVEY_LIMITS.optionLabelMaxLength))}
            maxLength={SURVEY_LIMITS.optionLabelMaxLength}
          />
          {options.length > SURVEY_LIMITS.optionsMin && (
            <Pressable
              onPress={() => remove(i)}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={`Remove option ${i + 1}`}
            >
              <Ionicons name="close-circle" size={22} color={colors.accent.red} />
            </Pressable>
          )}
        </View>
      ))}
      {options.length < SURVEY_LIMITS.optionsMax && (
        <Button
          title="+ Add option"
          variant="outline"
          size="small"
          onPress={add}
          style={styles.addOptionBtn}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  cardSpacing: { marginVertical: REDESIGN_SPACING.sectionGap / 2 },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: REDESIGN_SPACING.sectionGap,
  },
  typeBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  promptInput: {
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 64,
    textAlignVertical: 'top',
    marginBottom: REDESIGN_SPACING.sectionGap,
  },
  requiredRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: REDESIGN_SPACING.sectionGap,
  },
  longTextConfig: { marginTop: REDESIGN_SPACING.sectionGap },
  numberInput: {
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 12,
    paddingVertical: 8,
    width: 120,
  },
  optionsBlock: { marginTop: REDESIGN_SPACING.sectionGap, gap: REDESIGN_SPACING.sectionGap / 2 },
  optionRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  optionInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  addOptionBtn: { alignSelf: 'flex-start', marginTop: 4 },
});
